// src/pages/ScanDashboard.jsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./ScanDashboard.css";
import TemplateModal from "../../components/TemplateModal/TemplateModal";
import Toast from "../../components/Toast/Toast";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import CreateTemplateModal from "../../components/CreateTemplateModal/CreateTemplateModal";
import TemplateManagerModal from "../../components/TemplateManagerModal/TemplateManagerModal";

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  EditIcon,
  OcrIcon,
  RefreshIcon,
  SaveIcon,
  ScanIcon,
  UploadIcon,
} from "../../assets/icons";
import { API_BASE_URL, buildApiUrl } from "../../config/env";
import { useLanguage } from "../../contexts/language/LanguageContext";
import { useSettings } from "../../contexts/setting/SettingContext";
import { getAccessToken, logout } from "../../services/auth";
import {
  finalizeSessionNaming,
  getNamingRules,
  patchSessionMetadata,
  previewSessionNaming,
} from "../../services/naming";

const NAMING_STATUS = {
  NOT_PREVIEWED: "not_previewed",
  PREVIEWED: "previewed",
  FINALIZED: "finalized",
  OUTDATED: "outdated",
};

const defaultMetadataState = {
  record_code: "",
  document_type: "",
  physical_location_code: "",
  selected_document_rule_code: "",
  selected_batch_rule_code: "",
  naming_metadata_text: "{}",
};

const safeJsonStringify = (value) => {
  try {
    return JSON.stringify(value || {}, null, 2);
  } catch {
    return "{}";
  }
};

const normalizeWarnings = (warnings) => {
  if (!warnings) return [];
  if (Array.isArray(warnings)) return warnings.map((w) => String(w));
  if (typeof warnings === "string") return [warnings];
  if (typeof warnings === "object") {
    return Object.entries(warnings).map(
      ([key, value]) => `${key}: ${String(value)}`,
    );
  }
  return [];
};

const normalizeNamingDocuments = (payload) => {
  const groups =
    payload?.documents || payload?.document_groups || payload?.items || [];
  if (!Array.isArray(groups)) return [];

  return groups.map((item, idx) => ({
    doc_index:
      typeof item.doc_index === "number"
        ? item.doc_index
        : typeof item.doc_group_index === "number"
          ? item.doc_group_index
          : idx,
    doc_seq: item.doc_seq ?? idx + 1,
    logical_document_name:
      item.logical_document_name || item.document_name || item.filename || "",
    warnings: normalizeWarnings(item.warnings),
  }));
};

const NAMING_FIXED_TOKENS = new Set([
  "record_code",
  "document_type",
  "physical_location_code",
  "doc_seq",
  "doc_index",
]);

const extractPatternTokens = (pattern = "") => {
  const regex = /\{([a-zA-Z0-9_]+)(?::[^}]+)?\}/g;
  const tokens = [];
  let match;
  while ((match = regex.exec(pattern)) !== null) {
    const token = String(match[1] || "").trim();
    if (!token || NAMING_FIXED_TOKENS.has(token)) continue;
    if (!tokens.includes(token)) tokens.push(token);
  }
  return tokens;
};

const extractMissingTokens = (warnings = []) => {
  const missing = [];
  warnings.forEach((warning) => {
    const text = String(warning || "");
    const match = text.match(/Missing token:\s*([a-zA-Z0-9_]+)/i);
    if (!match?.[1]) return;
    const token = match[1];
    if (!missing.includes(token)) missing.push(token);
  });
  return missing;
};

const formatTimeHms = (value) => {
  if (!value) return "";

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toLocaleTimeString("vi-VN", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  const match = String(value).match(/(\d{2}:\d{2}:\d{2})/);
  return match?.[1] || String(value);
};

export default function ScanDashboard() {
  // --- Contexts & Env ---
  const { t } = useLanguage();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const wsUrl = settings.wsUrl;
  const wsToken = settings.wsToken;
  const baseUrl = API_BASE_URL;

  // --- Constants ---
  const LS_SID_KEY = "scan.sid";
  const LS_URLS_KEY = "scan.imageUrls";

  // --- State: UI & Modals ---
  const [openModal, setOpenModal] = useState(false);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openManagerModal, setOpenManagerModal] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  // --- State: Loading & Popups ---
  const [globalLoading, setGlobalLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [showExistingPagesPopup, setShowExistingPagesPopup] = useState(false);
  const [showNamingPopup, setShowNamingPopup] = useState(false);

  // --- State: Session Data ---
  const [currentSession, setCurrentSession] = useState(null);
  const [allImageUrls, setAllImageUrls] = useState([]); // Server URLs
  const [documents, setDocuments] = useState([]);
  const [templateFields, setTemplateFields] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedDocIndex, setSelectedDocIndex] = useState(null);
  const [sessionMetadata, setSessionMetadata] = useState(defaultMetadataState);
  const [namingMetadataValues, setNamingMetadataValues] = useState({});
  const [dynamicTokenSchema, setDynamicTokenSchema] = useState([]);
  const [missingNamingTokens, setMissingNamingTokens] = useState([]);
  const [namingState, setNamingState] = useState({
    status: NAMING_STATUS.NOT_PREVIEWED,
    batch_name: "",
    documents: [],
    warnings: [],
    stale_reason: "",
  });
  const [namingBusy, setNamingBusy] = useState(false);
  const [namingRules, setNamingRules] = useState([]);
  const [metadataFinalizeInfo, setMetadataFinalizeInfo] = useState({
    isFinalized: false,
    finalizedAt: "",
  });

  // --- State: Scanner & Process ---
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [wsConnected, setWsConnected] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [ocrDone, setOcrDone] = useState(false);
  const [ocrMode, setOcrMode] = useState("default");
  const [fieldsEditable, setFieldsEditable] = useState(false);

  // --- Refs ---
  const currentSessionRef = useRef(null);
  const selectedDeviceRef = useRef("");
  const wsRef = useRef(null);
  const wrapperRef = useRef(null);
  const imageRef = useRef(null);

  // --- Refs: Timers & Queues ---
  const confirmTimerRef = useRef(null);
  const initialTimerRef = useRef(null);
  const lastPageAtRef = useRef(0);
  const pendingUploadsRef = useRef(0);
  const uploadQueueRef = useRef(Promise.resolve());

  const getSessionId = (session) => session?._id || session?.session_id;

  const fetchSessionMetadata = async (sid) => {
    const res = await fetch(buildApiUrl(`sessions/${sid}/metadata`), {
      method: "GET",
      headers: { accept: "application/json" },
    });
    const data = await res.json().catch(() => null);
    if (!res.ok || !data) return null;
    return data;
  };

  const hydrateMetadataFromSession = (session, metadata = null) => {
    const metadataObj =
      metadata && typeof metadata === "object" ? metadata : {};
    const namingMetadataObj =
      metadataObj?.naming_metadata &&
      typeof metadataObj.naming_metadata === "object"
        ? metadataObj.naming_metadata
        : session?.naming_metadata &&
            typeof session.naming_metadata === "object"
          ? session.naming_metadata
          : {};
    setNamingMetadataValues(namingMetadataObj);

    setSessionMetadata({
      record_code: metadataObj.record_code || session?.record_code || "",
      document_type: metadataObj.document_type || session?.document_type || "",
      physical_location_code:
        metadataObj.physical_location_code ||
        session?.physical_location_code ||
        "",
      selected_document_rule_code:
        metadataObj.selected_document_rule_code ||
        session?.selected_document_rule_code ||
        "",
      selected_batch_rule_code:
        metadataObj.selected_batch_rule_code ||
        session?.selected_batch_rule_code ||
        "",
      naming_metadata_text: safeJsonStringify(namingMetadataObj),
    });

    setMetadataFinalizeInfo({
      isFinalized: Boolean(
        metadataObj?.is_naming_finalized ||
        session?.is_naming_finalized ||
        session?.naming_finalized,
      ),
      finalizedAt:
        metadataObj?.naming_finalized_at || session?.naming_finalized_at || "",
    });
  };

  const resetNamingState = () => {
    setNamingState({
      status: NAMING_STATUS.NOT_PREVIEWED,
      batch_name: "",
      documents: [],
      warnings: [],
      stale_reason: "",
    });
  };

  const hydrateNamingFromSession = (session, metadata = null) => {
    const metadataObj =
      metadata && typeof metadata === "object" ? metadata : {};
    const rawStatus = String(session?.naming_status || "").toLowerCase();
    const isFinalized = Boolean(
      metadataObj?.is_naming_finalized ||
      session?.is_naming_finalized ||
      session?.naming_finalized ||
      rawStatus === "finalized",
    );
    const hasPreview = Boolean(
      session?.naming_preview ||
      rawStatus === "previewed" ||
      rawStatus === "outdated" ||
      session?.batch_name ||
      session?.logical_documents,
    );

    const status = isFinalized
      ? NAMING_STATUS.FINALIZED
      : rawStatus === "outdated"
        ? NAMING_STATUS.OUTDATED
        : hasPreview
          ? NAMING_STATUS.PREVIEWED
          : NAMING_STATUS.NOT_PREVIEWED;

    const payload = session?.naming_preview
      ? session.naming_preview
      : {
          batch_name: session?.batch_name || session?.naming_batch_name || "",
          documents: Array.isArray(session?.logical_documents)
            ? session.logical_documents
            : [],
          warnings: session?.naming_warnings,
        };
    setNamingState({
      status,
      batch_name:
        payload?.batch_name ||
        session?.batch_name ||
        session?.naming_batch_name ||
        "",
      documents: normalizeNamingDocuments(payload),
      warnings: normalizeWarnings(
        payload?.warnings || session?.naming_warnings,
      ),
      stale_reason: session?.naming_stale_reason || "",
    });
  };

  const markNamingOutdated = (reason = "") => {
    setNamingState((prev) => {
      if (
        prev.status === NAMING_STATUS.FINALIZED ||
        prev.status === NAMING_STATUS.PREVIEWED
      ) {
        return {
          ...prev,
          status: NAMING_STATUS.OUTDATED,
          stale_reason: reason,
        };
      }
      return prev;
    });
  };

  const unknownNameDetected = (name = "") => /unknown/i.test(String(name));

  const namingHasUnknown =
    unknownNameDetected(namingState.batch_name) ||
    namingState.documents.some((doc) =>
      unknownNameDetected(doc.logical_document_name),
    );

  const activeNamingRules = namingRules.filter(
    (rule) => rule.is_active !== false,
  );
  const activeDocumentRules = activeNamingRules.filter(
    (rule) => rule.target === "logical_document_name",
  );
  const activeBatchRules = activeNamingRules.filter(
    (rule) => rule.target === "batch_name",
  );

  const updateSessionMetadata = (field, value) => {
    setSessionMetadata((prev) => ({
      ...prev,
      [field]: value,
    }));
    markNamingOutdated(t("naming_stale_reason_metadata"));
  };

  const selectNamingRule = (target, rule) => {
    if (!rule?.code) return;

    if (target === "logical_document_name") {
      setSessionMetadata((prev) => ({
        ...prev,
        selected_document_rule_code: rule.code,
        document_type: rule.document_type || prev.document_type || "",
      }));
    }

    if (target === "batch_name") {
      setSessionMetadata((prev) => ({
        ...prev,
        selected_batch_rule_code: rule.code,
        document_type: prev.document_type || rule.document_type || "",
      }));
    }

    markNamingOutdated(t("naming_stale_reason_metadata"));
  };

  const selectedDocumentRule = activeDocumentRules.find(
    (rule) => rule.code === sessionMetadata.selected_document_rule_code,
  );
  const selectedBatchRule = activeBatchRules.find(
    (rule) => rule.code === sessionMetadata.selected_batch_rule_code,
  );

  const isNamingFinalized =
    namingState.status === NAMING_STATUS.FINALIZED ||
    metadataFinalizeInfo.isFinalized;
  const finalizedTimeLabel = formatTimeHms(metadataFinalizeInfo.finalizedAt);
  const namingBatchPreview = namingState.batch_name || "-";
  const namingDocumentCount = namingState.documents.length;

  const updateDynamicTokenValue = (token, value) => {
    setNamingMetadataValues((prev) => ({
      ...prev,
      [token]: value,
    }));
    setMissingNamingTokens((prev) => prev.filter((item) => item !== token));
    markNamingOutdated(t("naming_stale_reason_metadata"));
  };

  // URL Query Helpers
  const getSidFromUrl = () => {
    try {
      return new URL(window.location.href).searchParams.get("sid");
    } catch {
      return null;
    }
  };

  const setSidToUrl = (sid) => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("sid", sid);
      window.history.replaceState({}, "", u.toString());
    } catch {}
  };

  const clearSidFromUrl = () => {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("sid");
      window.history.replaceState({}, "", u.toString());
    } catch {}
  };

  // LocalStorage Helpers
  const saveUrlsToLocal = (sid, urls) => {
    if (!sid) return;
    try {
      localStorage.setItem(LS_SID_KEY, String(sid));
      localStorage.setItem(`${LS_URLS_KEY}.${sid}`, JSON.stringify(urls || []));
    } catch {}
  };

  const loadUrlsFromLocal = (sid) => {
    if (!sid) return [];
    try {
      const raw = localStorage.getItem(`${LS_URLS_KEY}.${sid}`);
      const arr = JSON.parse(raw || "[]");
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  // Path Helpers
  const toFullUrl = (p) => {
    if (!p) return "";
    const b = (baseUrl || "").replace(/\/+$/, "");
    const pp = String(p).replace(/^\/+/, "");
    if (pp.startsWith("storage/"))
      return `${b}/static/${pp.replace(/^storage\//, "")}`;
    if (pp.startsWith("static/")) return `${b}/${pp}`;
    if (/^https?:\/\//i.test(pp)) return pp;
    return `${b}/${pp}`;
  };

  const toStaticFullUrl = (p) => {
    if (!p) return "";
    const b = (baseUrl || "").replace(/\/+$/, "");
    const pp = String(p).trim();
    if (pp.startsWith("/static/")) return `${b}${pp}`;
    if (pp.startsWith("static/")) return `${b}/${pp}`;
    if (pp.startsWith("storage/"))
      return `${b}/static/${pp.replace(/^storage\//, "")}`;
    if (/^https?:\/\//i.test(pp)) return pp;
    return `${b}/${pp.replace(/^\/+/, "")}`;
  };

  const resolveOcrMode = (data) =>
    data?.internal_mode || data?.current_mode || data?.mode || null;

  const optimizeScanBlob = async (blob) => {
    if (!(blob instanceof Blob)) return blob;
    if (!/^image\//i.test(blob.type || "")) return blob;
    if (typeof createImageBitmap !== "function") return blob;

    try {
      const bitmap = await createImageBitmap(blob);
      const maxDimension = blob.size > 2 * 1024 * 1024 ? 1800 : 2200;
      const scale = Math.min(
        1,
        maxDimension / Math.max(bitmap.width || 1, bitmap.height || 1),
      );
      const width = Math.max(1, Math.round(bitmap.width * scale));
      const height = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");

      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      if (!context) {
        bitmap.close?.();
        return blob;
      }

      context.fillStyle = "#fff";
      context.fillRect(0, 0, width, height);
      context.drawImage(bitmap, 0, 0, width, height);
      bitmap.close?.();

      const compressed = await new Promise((resolve) => {
        canvas.toBlob(
          resolve,
          "image/jpeg",
          blob.size > 2 * 1024 * 1024 ? 0.72 : 0.8,
        );
      });

      if (compressed instanceof Blob && compressed.size > 0) {
        return compressed.size < blob.size ? compressed : blob;
      }

      return blob;
    } catch {
      return blob;
    }
  };

  // Document Structure Helper
  const buildDocsFromStructure = (structure, fields) => {
    const s = Array.isArray(structure) ? structure : [];
    return s.map((indices, idx) => ({
      doc_index: idx,
      indices: Array.isArray(indices)
        ? indices.filter((x) => typeof x === "number")
        : [],
      fields: (fields || []).map((f) => ({ ...f, value: "" })),
    }));
  };

  const scheduleInactivityPopup = () => {
    if (confirmTimerRef.current) {
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = null;
    }
    const check = () => {
      const elapsed = Date.now() - (lastPageAtRef.current || 0);
      if (elapsed >= 2000) {
        if (pendingUploadsRef.current > 0) {
          confirmTimerRef.current = setTimeout(check, 500);
          return;
        }
        confirmTimerRef.current = null;
        setShowConfirmPopup(true);
      } else {
        confirmTimerRef.current = setTimeout(check, 2000 - elapsed);
      }
    };
    confirmTimerRef.current = setTimeout(check, 2000);
  };

  // ---------------- Restore after refresh (sid + images from localStorage) ----------------
  useEffect(() => {
    const restore = async () => {
      // 1) Prefer the sid from the URL, then fall back to localStorage.
      let sid = getSidFromUrl();
      if (!sid) {
        try {
          sid = localStorage.getItem(LS_SID_KEY) || null;
          if (sid) setSidToUrl(sid);
        } catch {}
      }
      if (!sid) return;

      // 2) Load cached URLs from localStorage.
      const cachedUrls = loadUrlsFromLocal(sid);

      console.log("[RESTORE] baseUrl:", baseUrl);
      console.log("[RESTORE] sid:", sid);
      console.log("[LS] raw:", localStorage.getItem(`${LS_URLS_KEY}.${sid}`));
      console.log("[LS] parsed urls:", cachedUrls);

      // show cached urls immediately
      if (cachedUrls.length) {
        setAllImageUrls(cachedUrls);
        setSelectedImage(cachedUrls[0] || null);
      }

      // 3) Fetch session details to restore documents.fields values.
      try {
        const res = await fetch(buildApiUrl(`sessions/${sid}`), {
          method: "GET",
        });
        const data = await res.json().catch(() => null);

        if (!res.ok || !data) {
          setToastMsg(
            (data && data.detail) || t("toast_session_restore_failed"),
          );
          return;
        }

        console.log("[RESTORE] raw data:", data);

        let metadataResponse = null;
        try {
          metadataResponse = await fetchSessionMetadata(sid);
        } catch (error) {
          console.error("[RESTORE] metadata fetch failed:", error);
        }

        // Store the restored session.
        setCurrentSession(data);
        currentSessionRef.current = data;
        hydrateMetadataFromSession(data, metadataResponse);
        hydrateNamingFromSession(data, metadataResponse);

        // Keep the sid in both the URL and localStorage.
        setSidToUrl(sid);
        try {
          localStorage.setItem(LS_SID_KEY, String(sid));
        } catch {}

        setOcrDone(!!data.ocr_done);

        // Restore template fields when available.
        const fields =
          data.indexes || data.index_fields || data.template_fields || [];
        if (Array.isArray(fields) && fields.length) setTemplateFields(fields);

        // 4) Prefer backend documents because they already include field values.
        if (Array.isArray(data.documents) && data.documents.length) {
          const docs = data.documents.map((d, idx) => ({
            doc_index: typeof d.doc_index === "number" ? d.doc_index : idx,
            indices: Array.isArray(d.indices) ? d.indices : [],
            fields: Array.isArray(d.fields) ? d.fields : [],
          }));

          setDocuments(docs);

          // Auto-select the first document.
          const firstDocIdx = docs[0]?.doc_index ?? 0;
          setSelectedDocIndex(firstDocIdx);

          // Auto-select the first image referenced by indices[0].
          const urlsToUse = cachedUrls.length ? cachedUrls : allImageUrls;
          const firstPageIndex = docs[0]?.indices?.[0];

          if (typeof firstPageIndex === "number") {
            setSelectedImage(urlsToUse[firstPageIndex] || urlsToUse[0] || null);
          } else {
            setSelectedImage(urlsToUse[0] || null);
          }

          return; // Stop here to avoid overwriting documents with fallback data.
        }

        // 5) Fall back when the backend does not return documents.
        const structure = data.document_structure || [];
        const docs = buildDocsFromStructure(
          structure,
          fields.length ? fields : templateFields,
        );
        setDocuments(docs);

        if (docs.length) {
          setSelectedDocIndex(docs[0]?.doc_index ?? 0);
          const firstPageIndex = docs[0]?.indices?.[0];
          const urlsToUse = cachedUrls.length ? cachedUrls : allImageUrls;
          if (typeof firstPageIndex === "number")
            setSelectedImage(urlsToUse[firstPageIndex] || urlsToUse[0] || null);
          else setSelectedImage(urlsToUse[0] || null);
        }

        // Optionally refresh URLs and localStorage when file_paths are returned.
        const filePaths =
          data?.file_paths || data?.files || data?.image_paths || [];
        const urls = Array.isArray(filePaths) ? filePaths.map(toFullUrl) : [];
        console.log("[RESTORE] be filePaths:", filePaths);
        console.log("[RESTORE] be image urls:", urls);

        if (urls.length) {
          setAllImageUrls(urls);
          saveUrlsToLocal(sid, urls);
        }
      } catch (e) {
        console.error("[RESTORE] error:", e);
        setToastMsg(t("toast_session_restore_connection_error"));
      }
    };
    if (baseUrl) restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  // Keep refs in sync with state.
  useEffect(() => {
    currentSessionRef.current = currentSession;
  }, [currentSession]);

  useEffect(() => {
    if (!currentSession) return;
    const sid = getSessionId(currentSession);
    if (!sid) {
      hydrateMetadataFromSession(currentSession);
      hydrateNamingFromSession(currentSession);
      return;
    }

    fetchSessionMetadata(sid)
      .then((metadata) => {
        if (!metadata) return;
        hydrateMetadataFromSession(currentSession, metadata);
        hydrateNamingFromSession(currentSession, metadata);
      })
      .catch(() => {});
  }, [currentSession]);

  useEffect(() => {
    const nextDocumentType =
      selectedDocumentRule?.document_type ||
      selectedBatchRule?.document_type ||
      "";

    setSessionMetadata((prev) =>
      prev.document_type === nextDocumentType
        ? prev
        : {
            ...prev,
            document_type: nextDocumentType,
          },
    );
  }, [selectedDocumentRule?.document_type, selectedBatchRule?.document_type]);

  useEffect(() => {
    const patterns = [selectedDocumentRule?.pattern, selectedBatchRule?.pattern]
      .filter(Boolean)
      .join(" ");
    const tokens = extractPatternTokens(patterns);
    setDynamicTokenSchema(tokens);
    setNamingMetadataValues((prev) => {
      const next = { ...(prev || {}) };
      tokens.forEach((token) => {
        if (typeof next[token] === "undefined") next[token] = "";
      });
      return next;
    });
  }, [selectedDocumentRule?.pattern, selectedBatchRule?.pattern]);

  useEffect(() => {
    selectedDeviceRef.current = selectedDevice;
  }, [selectedDevice]);

  // Requirement #5: log the session id and image URLs.
  useEffect(() => {
    console.log(
      "[STATE] sessionId:",
      currentSession?._id || currentSession?.session_id,
    );
    console.log("[STATE] allImageUrls:", allImageUrls);
  }, [currentSession, allImageUrls]);

  // ---------------- OCR mode init ----------------
  useEffect(() => {
    const fetchCurrentMode = async () => {
      try {
        const res = await fetch(buildApiUrl("system/current-mode"));
        const data = await res.json().catch(() => null);
        const currentMode = resolveOcrMode(data);
        if (res.ok && currentMode) setOcrMode(currentMode);
      } catch {}
    };
    if (baseUrl) fetchCurrentMode();
  }, [baseUrl]);

  useEffect(() => {
    const fetchNamingRules = async () => {
      try {
        const data = await getNamingRules();
        setNamingRules(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Load naming rules failed", error);
      }
    };

    if (baseUrl) fetchNamingRules();
  }, [baseUrl]);

  useEffect(() => {
    const sid = getSessionId(currentSessionRef.current);
    if (!sid || !isNamingFinalized) return;

    const hasPreviewData =
      Boolean(namingState.batch_name) ||
      namingState.documents.some(
        (doc) =>
          Boolean(doc.logical_document_name) ||
          (Array.isArray(doc.warnings) && doc.warnings.length > 0),
      );
    if (hasPreviewData) return;

    let cancelled = false;
    const loadLockedPreview = async () => {
      try {
        const payload = await previewSessionNaming(sid);
        if (cancelled) return;
        setNamingState((prev) => ({
          ...prev,
          status: NAMING_STATUS.FINALIZED,
          batch_name: payload?.batch_name || prev.batch_name,
          documents:
            normalizeNamingDocuments(payload).length > 0
              ? normalizeNamingDocuments(payload)
              : prev.documents,
          warnings: normalizeWarnings(payload?.warnings),
          stale_reason: "",
        }));
      } catch {
        // Keep existing state if preview API fails.
      }
    };

    loadLockedPreview();
    return () => {
      cancelled = true;
    };
  }, [isNamingFinalized, namingState.batch_name, namingState.documents.length]);

  // ================== TEMPLATE ==================
  const handleSelectTemplate = (data) => {
    fetch(buildApiUrl("sessions/"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        package_id: data.templateId,
        name: data.batchName || "",
      }),
    })
      .then(async (res) => {
        const payload = await res.json().catch(() => null);
        if (!res.ok) {
          setToastMsg(
            (payload && payload.detail) || t("toast_create_session_failed"),
          );
          return null;
        }
        return payload;
      })
      .then((session) => {
        if (!session) return;

        console.log("[CREATE] raw session:", session);

        setCurrentSession(session);
        currentSessionRef.current = session;
        hydrateMetadataFromSession(session);
        resetNamingState();

        const sid = session?._id || session?.session_id;
        if (sid) {
          setSidToUrl(sid);
          try {
            localStorage.setItem(LS_SID_KEY, String(sid));
          } catch {}
        }

        // template fields if returned
        setTemplateFields(
          session.indexes ||
            session.index_fields ||
            session.template_fields ||
            [],
        );

        // reset view for new session
        setDocuments([]);
        setSelectedDocIndex(null);
        setSelectedImage(null);
        setAllImageUrls([]);
        setFieldsEditable(false);
        setOcrDone(false);
        setNamingMetadataValues({});
        setMissingNamingTokens([]);

        setToastMsg(t("toast_create_session_success"));
      })
      .catch(() => setToastMsg(t("toast_create_session_failed")));
  };

  // ================== UPLOAD 1 PAGE ==================
  const uploadOnePage = async (blob) => {
    const s = currentSessionRef.current;
    const sid = s?._id || s?.session_id;
    if (!sid) throw new Error("Chưa có session");

    const optimizedBlob = await optimizeScanBlob(blob);
    console.log("[UPLOAD] blob size:", {
      originalBytes: blob.size,
      originalType: blob.type,
      optimizedBytes: optimizedBlob.size,
      optimizedType: optimizedBlob.type,
    });
    const form = new FormData();
    const extension =
      optimizedBlob.type === "image/jpeg"
        ? "jpg"
        : optimizedBlob.type === "image/webp"
          ? "webp"
          : "png";
    const file = new File([optimizedBlob], `scan-${Date.now()}.${extension}`, {
      type: optimizedBlob.type || blob.type || "image/png",
    });

    // BE requires "files"
    form.append("files", file);

    const endpoint = buildApiUrl(`sessions/${sid}/upload`);

    const res = await fetch(endpoint, { method: "POST", body: form });
    const data = await res.json().catch(() => null);
    if (!res.ok)
      throw new Error((data && data.detail) || t("toast_upload_page_failed"));

    return data; // {session_id, total_files, new_files, document_structure, ...}
  };

  // ================== API UPDATE STRUCTURE ==================
  const updateStructureAPI = async (newDocuments) => {
    const s = currentSessionRef.current;
    const sid = s?._id || s?.session_id;
    if (!sid) return;

    const newStructure = newDocuments.map((doc) =>
      (doc.indices || []).map((i) => i),
    );

    try {
      const res = await fetch(buildApiUrl(`sessions/${sid}/structure`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_structure: newStructure }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        console.error("Update structure failed:", data);
        setToastMsg(
          (data && data.detail) || t("toast_update_structure_failed"),
        );
      } else {
        markNamingOutdated(t("naming_stale_reason_structure"));
      }
    } catch {
      setToastMsg(t("toast_update_structure_conn_err"));
    }
  };

  // ================== DRAG DROP ==================
  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    let newDocuments = JSON.parse(JSON.stringify(documents));

    const sourceDocIndex = parseInt(source.droppableId.replace("doc-", ""), 10);
    const sourceDoc = newDocuments.find((d) => d.doc_index === sourceDocIndex);
    if (!sourceDoc) return;

    const [movedPageIndex] = sourceDoc.indices.splice(source.index, 1);

    if (destination.droppableId === "create-new-doc") {
      const newDoc = {
        doc_index: newDocuments.length,
        indices: [movedPageIndex],
        fields: templateFields.map((f) => ({ ...f, value: "" })),
      };
      newDocuments.push(newDoc);
    } else {
      const destDocIndex = parseInt(
        destination.droppableId.replace("doc-", ""),
        10,
      );
      const destDoc = newDocuments.find((d) => d.doc_index === destDocIndex);
      if (!destDoc) {
        sourceDoc.indices.splice(source.index, 0, movedPageIndex);
        return;
      }
      destDoc.indices.splice(destination.index, 0, movedPageIndex);
    }

    newDocuments = newDocuments.filter((doc) => (doc.indices || []).length > 0);
    newDocuments = newDocuments.map((doc, index) => ({
      ...doc,
      doc_index: index,
    }));

    setDocuments(newDocuments);
    if (typeof movedPageIndex === "number")
      setSelectedImage(allImageUrls[movedPageIndex] || null);

    if (!newDocuments.find((d) => d.doc_index === selectedDocIndex)) {
      setSelectedDocIndex(newDocuments.length ? 0 : null);
    }

    updateStructureAPI(newDocuments);
  };

  // ================== WS SEND SCAN ==================
  const sendScanCommand = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "scan",
          token: wsToken,
          device: selectedDeviceRef.current,
          source: "glass",
          dpi: 200,
          bitdepth: "color",
        }),
      );
      return true;
    }
    return false;
  };

  // ================== SCAN ==================
  const handleScan = async () => {
    const s = currentSessionRef.current;
    const sid = s?._id || s?.session_id;
    if (!sid) return setToastMsg(t("toast_select_template_required"));
    if (!wsRef.current || !wsConnected)
      return setToastMsg(t("toast_scanner_not_connected"));
    if (!selectedDeviceRef.current)
      return setToastMsg(t("toast_select_scanner_required"));

    setIsScanning(true);
    setGlobalLoading(true);

    setLoadingText(t("loading_scanning"));

    const ok = sendScanCommand();
    if (!ok) {
      setToastMsg(t("toast_scanner_not_ready"));
      setGlobalLoading(false);
      setIsScanning(false);
      return;
    }
    const currentCount = allImageUrls.length;
    if (initialTimerRef.current) clearTimeout(initialTimerRef.current);
    initialTimerRef.current = setTimeout(() => {
      initialTimerRef.current = null;
      if (allImageUrls.length === currentCount) {
        setShowConfirmPopup(true);
      }
    }, 30000);
  };

  // ================== OCR ==================
  const runOcr = async () => {
    const s = currentSessionRef.current;
    const sid = s?._id || s?.session_id;
    if (!sid) return;

    const fileIndices = documents.flatMap((d) => d.indices || []);
    if (!fileIndices.length) return;

    const res = await fetch(buildApiUrl("ocr/extract"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        session_id: sid,
        file_indices: fileIndices,
      }),
    });

    const data = await res.json().catch(() => null);
    if (!res.ok)
      throw new Error((data && data.detail) || t("toast_ocr_failed"));

    const processed = data.processed_groups || [];
    setDocuments((prev) =>
      prev.map((doc) => {
        const group = processed.find(
          (g) => g.doc_group_index === doc.doc_index,
        );
        if (!group || !group.extracted_data) return doc;

        const updates = group.extracted_data;
        return {
          ...doc,
          fields: (doc.fields || []).map((f) => ({
            ...f,
            value: updates[f.key] ?? f.value ?? "",
          })),
        };
      }),
    );

    setOcrDone(true);
  };

  const handleOcr = async () => {
    const s = currentSessionRef.current;
    const sid = s?._id || s?.session_id;
    if (!sid) return setToastMsg(t("toast_no_session"));
    if (!documents.length || !allImageUrls.length)
      return setToastMsg(t("toast_ocr_no_data"));

    setGlobalLoading(true);
    setLoadingText(t("loading_ocr_all"));
    try {
      await runOcr();
      setFieldsEditable(false);
      setToastMsg(t("toast_ocr_success"));
    } catch (e) {
      console.error(e);
      setToastMsg(e?.message || t("toast_ocr_conn_err"));
    } finally {
      setGlobalLoading(false);
    }
  };

  const switchOcrMode = async (mode) => {
    try {
      const res = await fetch(buildApiUrl(`system/switch-mode?mode=${mode}`), {
        method: "POST",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) return null;
      setOcrMode(resolveOcrMode(data) || mode);
      setToastMsg(data.message || t("toast_ocr_mode_switched", { mode: mode }));
      return data;
    } catch {
      return null;
    }
  };

  const handleOcrVip = async () => {
    if (!currentSessionRef.current || !documents.length) return;
    const newMode = ocrMode === "default" ? "vip" : "default";
    const switched = await switchOcrMode(newMode);
    if (switched)
      setToastMsg(
        `Chế độ OCR đã chuyển sang: ${newMode === "vip" ? "VIP" : "DEFAULT"}`,
      );
  };

  // ================== EDIT / SAVE ==================
  const currentDoc =
    documents.find((d) => d.doc_index === selectedDocIndex) || null;

  const namingStatusLabel = isNamingFinalized
    ? t("naming_status_finalized")
    : namingState.status === NAMING_STATUS.PREVIEWED
      ? t("naming_status_previewed")
      : namingState.status === NAMING_STATUS.OUTDATED
        ? t("naming_status_outdated")
        : t("naming_status_not_previewed");
  const documentNamingMap = new Map(
    namingState.documents.map((doc) => [
      doc.doc_index,
      String(doc.logical_document_name || "").trim(),
    ]),
  );

  const handleEditOrSave = async () => {
    const s = currentSessionRef.current;
    const sid = s?._id || s?.session_id;
    if (!sid) return;

    const doc =
      documents.find((d) => d.doc_index === selectedDocIndex) || documents[0];
    if (!doc) return setToastMsg(t("toast_select_doc_to_edit"));

    if (!fieldsEditable) {
      setFieldsEditable(true);
      return;
    }

    const updates = {};
    (doc.fields || []).forEach((f) => {
      if (!f.key) return;
      updates[f.key] = f.value ?? "";
    });

    try {
      const res = await fetch(buildApiUrl(`sessions/${sid}/ocr`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ doc_index: doc.doc_index, updates }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok)
        return setToastMsg((data && data.detail) || t("toast_save_failed"));
      setFieldsEditable(false);
      setToastMsg(t("toast_save_success"));
    } catch {
      setToastMsg(t("toast_conn_error"));
    }
  };

  const handleSaveMetadata = async () => {
    const sid = getSessionId(currentSessionRef.current);
    if (!sid) {
      setToastMsg(t("toast_no_session"));
      return;
    }

    const namingMetadata = Object.entries(namingMetadataValues || {}).reduce(
      (acc, [key, value]) => {
        if (!key) return acc;
        if (value === undefined || value === null || value === "") return acc;
        acc[key] = value;
        return acc;
      },
      {},
    );

    setNamingBusy(true);
    try {
      const payload = {
        record_code: sessionMetadata.record_code || undefined,
        document_type: sessionMetadata.document_type || undefined,
        physical_location_code:
          sessionMetadata.physical_location_code || undefined,
        selected_document_rule_code:
          sessionMetadata.selected_document_rule_code || undefined,
        selected_batch_rule_code:
          sessionMetadata.selected_batch_rule_code || undefined,
        naming_metadata: namingMetadata,
      };

      const data = await patchSessionMetadata(sid, payload);
      let latestSession = null;
      let latestMetadata = null;
      try {
        const [sessionResponse, metadataResponse] = await Promise.all([
          fetch(buildApiUrl(`sessions/${sid}`), { method: "GET" }),
          fetch(buildApiUrl(`sessions/${sid}/metadata`), {
            method: "GET",
            headers: { accept: "application/json" },
          }),
        ]);
        const sessionData = await sessionResponse.json().catch(() => null);
        const metadataData = await metadataResponse.json().catch(() => null);
        if (sessionResponse.ok && sessionData) latestSession = sessionData;
        if (metadataResponse.ok && metadataData) latestMetadata = metadataData;
      } catch {
        latestSession = null;
        latestMetadata = null;
      }

      const mergedSession = {
        ...(currentSessionRef.current || {}),
        ...(data || {}),
        ...payload,
      };
      const nextSession = latestSession || mergedSession;

      setCurrentSession(nextSession);
      currentSessionRef.current = nextSession;
      hydrateMetadataFromSession(nextSession, latestMetadata);
      setMissingNamingTokens([]);
      markNamingOutdated(t("naming_stale_reason_metadata"));
      setToastMsg(t("toast_naming_metadata_saved"));
    } catch (e) {
      setToastMsg(e?.message || t("toast_naming_metadata_save_failed"));
    } finally {
      setNamingBusy(false);
    }
  };

  const handlePreviewNaming = async () => {
    const sid = getSessionId(currentSessionRef.current);
    if (!sid) {
      setToastMsg(t("toast_no_session"));
      return;
    }

    setNamingBusy(true);
    try {
      const payload = await previewSessionNaming(sid);
      const warningPool = [
        ...normalizeWarnings(payload?.warnings),
        ...normalizeNamingDocuments(payload).flatMap((doc) =>
          normalizeWarnings(doc?.warnings),
        ),
      ];
      setMissingNamingTokens(extractMissingTokens(warningPool));
      setNamingState({
        status: NAMING_STATUS.PREVIEWED,
        batch_name: payload?.batch_name || "",
        documents: normalizeNamingDocuments(payload),
        warnings: normalizeWarnings(payload?.warnings),
        stale_reason: "",
      });
      setToastMsg(t("toast_naming_preview_success"));
    } catch (e) {
      setToastMsg(e?.message || t("toast_naming_preview_failed"));
    } finally {
      setNamingBusy(false);
    }
  };

  const handleFinalizeNaming = async () => {
    const sid = getSessionId(currentSessionRef.current);
    if (!sid) {
      setToastMsg(t("toast_no_session"));
      return;
    }

    setNamingBusy(true);
    try {
      const payload = await finalizeSessionNaming(sid);
      setNamingState((prev) => ({
        status: NAMING_STATUS.FINALIZED,
        batch_name: payload?.batch_name || prev.batch_name,
        documents:
          normalizeNamingDocuments(payload).length > 0
            ? normalizeNamingDocuments(payload)
            : prev.documents,
        warnings: normalizeWarnings(payload?.warnings),
        stale_reason: "",
      }));
      setMetadataFinalizeInfo({
        isFinalized: true,
        finalizedAt: payload?.naming_finalized_at || new Date().toISOString(),
      });
      setToastMsg(t("toast_naming_finalize_success"));
    } catch (e) {
      setToastMsg(e?.message || t("toast_naming_finalize_failed"));
    } finally {
      setNamingBusy(false);
    }
  };

  const handleExport = async () => {
    const s = currentSessionRef.current;
    const sid = s?._id || s?.session_id;
    if (!sid || !ocrDone) return setToastMsg(t("toast_export_ocr_required"));

    if (namingState.status !== NAMING_STATUS.FINALIZED) {
      const shouldContinue = window.confirm(
        t("naming_confirm_export_without_finalize"),
      );
      if (!shouldContinue) return;
    }

    if (namingHasUnknown) {
      const shouldContinueUnknown = window.confirm(
        t("naming_confirm_export_with_unknown"),
      );
      if (!shouldContinueUnknown) return;
    }

    const accessToken = getAccessToken();
    if (!accessToken) {
      logout();
      navigate("/login", { replace: true });
      return;
    }

    setGlobalLoading(true);
    setLoadingText(t("loading_exporting"));
    try {
      const res = await fetch(buildApiUrl(`sessions/${sid}/upload-paperless`), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await res.json().catch(() => null);
      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login", { replace: true });
        setToastMsg(
          (data && data.detail) || t("login_alert_invalid_credentials"),
        );
      } else if (!res.ok) {
        setToastMsg((data && data.detail) || t("toast_export_failed"));
      } else {
        setToastMsg((data && data.detail) || t("toast_export_success"));
      }
    } catch {
      setToastMsg(t("toast_conn_error"));
    } finally {
      setGlobalLoading(false);
    }
  };

  const handleResetSession = async () => {
    const oldSid =
      currentSessionRef.current?._id || currentSessionRef.current?.session_id;

    setCurrentSession(null);
    currentSessionRef.current = null;

    setDocuments([]);
    setAllImageUrls([]);
    setSelectedImage(null);
    setSelectedDocIndex(null);
    setFieldsEditable(false);
    setTemplateFields([]);
    setSessionMetadata(defaultMetadataState);
    setNamingMetadataValues({});
    setMissingNamingTokens([]);
    setMetadataFinalizeInfo({ isFinalized: false, finalizedAt: "" });
    resetNamingState();
    setIsScanning(false);
    setOcrDone(false);
    setGlobalLoading(false);

    clearSidFromUrl();

    try {
      localStorage.removeItem(LS_SID_KEY);
      if (oldSid) localStorage.removeItem(`${LS_URLS_KEY}.${oldSid}`);
    } catch {}

    setToastMsg(t("toast_reset_success"));
  };

  // ================== CONFIRM POPUP (INACTIVITY) helpers ==================
  const cancelContinueScan = () => {
    setShowConfirmPopup(false);
    setShowExistingPagesPopup(false);

    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(
          JSON.stringify({
            type: "stop",
            token: wsToken,
            device: selectedDeviceRef.current,
          }),
        );
      }
    } catch {}

    try {
      if (confirmTimerRef.current) {
        clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = null;
      }
    } catch {}
    try {
      if (initialTimerRef.current) {
        clearTimeout(initialTimerRef.current);
        initialTimerRef.current = null;
      }
    } catch {}

    lastPageAtRef.current = 0;

    setIsScanning(false);

    setLoadingText("");
    setToastMsg(t("toast_scan_paused"));
  };

  const confirmContinueScan = () => {
    setShowConfirmPopup(false);
    setShowExistingPagesPopup(false);

    setIsScanning(true);
    setGlobalLoading(true);
    setLoadingText(t("loading_resuming_scan"));

    const ok = sendScanCommand();
    if (!ok) {
      setToastMsg(t("toast_scanner_not_ready"));
      setGlobalLoading(false);
      setIsScanning(false);
    }
  };

  // ================== WS INIT ==================
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let mounted = true;
    let reconnectTimer = null;

    const connect = () => {
      if (!mounted) return;
      if (wsRef.current) return;

      try {
        const urlWithToken = wsUrl?.includes("?")
          ? `${wsUrl}&token=${encodeURIComponent(wsToken || "")}`
          : `${wsUrl}?token=${encodeURIComponent(wsToken || "")}`;

        const ws = new WebSocket(urlWithToken);
        wsRef.current = ws;
        setWsConnected(false);

        ws.onopen = () => {
          if (!mounted) return;
          setWsConnected(true);
          try {
            ws.send(JSON.stringify({ type: "list_devices", token: wsToken }));
          } catch {}
        };

        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);

            if (msg.type === "devices") {
              if (msg.ok) setDevices(msg.devices || []);
              return;
            }
            if (msg.type === "hello") return;

            if (msg.type === "done") {
              if (!msg.ok) {
                setToastMsg(msg.error || t("toast_scan_failed"));
                setGlobalLoading(false);
                setIsScanning(false);
                return;
              }

              let pagesToProcess = [];
              if (
                msg.pages &&
                Array.isArray(msg.pages) &&
                msg.pages.length > 0
              ) {
                pagesToProcess = msg.pages;
              } else if (msg.base64) {
                pagesToProcess = [msg.base64];
              }

              if (pagesToProcess.length === 0) return;

              pendingUploadsRef.current += pagesToProcess.length;

              if (initialTimerRef.current) {
                clearTimeout(initialTimerRef.current);
                initialTimerRef.current = null;
              }

              lastPageAtRef.current = Date.now();
              scheduleInactivityPopup();

              const contentType = msg.content_type || "image/png";
              setGlobalLoading(true);
              setLoadingText(t("loading_uploading_scanned_page"));
              pagesToProcess.forEach((base64Str) => {
                console.log(1);
                const bstr = atob(base64Str);
                let n = bstr.length;
                const u8arr = new Uint8Array(n);
                while (n--) u8arr[n] = bstr.charCodeAt(n);
                const blob = new Blob([u8arr], { type: contentType });

                uploadQueueRef.current = uploadQueueRef.current
                  .then(async () => {
                    const s = currentSessionRef.current;
                    const currentSid = s?._id || s?.session_id;
                    if (!currentSid)
                      throw new Error(t("toast_no_session_to_upload"));

                    const resp = await uploadOnePage(blob);

                    // resp schema: { session_id, total_files, new_files, document_structure }
                    console.log("[UPLOAD] raw response:", resp);
                    console.log("[UPLOAD] session_id:", resp?.session_id);
                    console.log("[UPLOAD] new_files:", resp?.new_files);

                    const sid = resp?.session_id || currentSid;
                    if (sid) {
                      setSidToUrl(sid);
                      try {
                        localStorage.setItem(LS_SID_KEY, String(sid));
                      } catch {}
                    }

                    const newFiles = Array.isArray(resp?.new_files)
                      ? resp.new_files
                      : [];
                    const newUrls = newFiles
                      .map(toStaticFullUrl)
                      .filter(Boolean);

                    console.log("[UPLOAD] newUrls(full):", newUrls);

                    // append urls + save local
                    if (sid && newUrls.length) {
                      setAllImageUrls((prev) => {
                        const merged = [...(prev || []), ...newUrls];
                        saveUrlsToLocal(sid, merged);

                        console.log("[LS] sid:", sid);
                        console.log("[LS] urls saved:", merged);
                        console.log(
                          "[LS] raw:",
                          localStorage.getItem(`${LS_URLS_KEY}.${sid}`),
                        );

                        return merged;
                      });
                      setSelectedImage(newUrls[newUrls.length - 1] || null);
                    }

                    // update docs by structure (if provided)
                    if (Array.isArray(resp?.document_structure)) {
                      const docs = buildDocsFromStructure(
                        resp.document_structure,
                        templateFields,
                      );
                      setDocuments(docs);
                      markNamingOutdated(t("naming_stale_reason_structure"));

                      // keep selected doc aligned with last page
                      const lastIndex =
                        allImageUrls.length + newUrls.length - 1;
                      const docIdx = resp.document_structure.findIndex(
                        (g) => Array.isArray(g) && g.includes(lastIndex),
                      );
                      if (docIdx >= 0) setSelectedDocIndex(docIdx);
                    }
                  })
                  .catch((e) => {
                    console.error(e);
                    setToastMsg(e?.message || t("toast_upload_page_failed"));
                  })
                  .finally(() => {
                    if (pendingUploadsRef.current > 0) {
                      pendingUploadsRef.current -= 1;
                    }
                  });
              });
            }
          } catch (e) {
            console.error("WS parse error", e);
          }
        };

        ws.onclose = () => {
          setWsConnected(false);
          if (wsRef.current === ws) wsRef.current = null;
          if (mounted) reconnectTimer = setTimeout(connect, 1500);
        };

        ws.onerror = () => {
          setWsConnected(false);
          try {
            if (wsRef.current === ws) wsRef.current = null;
          } catch {}
        };
      } catch (e) {
        if (mounted) reconnectTimer = setTimeout(connect, 1500);
      }
    };

    if (wsUrl) connect();

    return () => {
      mounted = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
  }, [wsUrl, wsToken, baseUrl, templateFields]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ================== RENDER ==================
  return (
    <div className="scan-container">
      {/* Toolbar */}

      <div className="toolbar">
        <div className="toolbar-left">
          <div className="dropdown">
            <button
              className="btn dropdown-toggle"
              onClick={() => setOpenModal(true)}
              title={t("toolbar_create_package_tooltip")}
            >
              {t("toolbar_create_package_btn")}
            </button>
          </div>
          {/* Scanner WS device selector */}
          <div className="selector-scanner-wrapper">
            {/* Select dropdown */}
            <select
              className="scanner-select"
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              disabled={!wsConnected} // Disable selection until the connection is ready.
            >
              <option value="">
                {wsConnected
                  ? t("toolbar_select_scanner_placeholder")
                  : t("toolbar_connecting_scanner")}
              </option>
              {devices.map((d, i) => (
                <option key={i} value={d}>
                  {d}
                </option>
              ))}
            </select>

            {/* Status badge */}
            <div
              className={`scanner-status ${wsConnected ? "is-connected" : "is-offline"}`}
            >
              <span className="status-dot"></span>
              <span className="status-text">
                {wsConnected
                  ? t("toolbar_ws_connected")
                  : t("toolbar_ws_offline")}
              </span>
            </div>
          </div>
        </div>
        <div className="toolbar-right">
          {currentSession && (
            <button
              className="btn ai"
              onClick={handleScan}
              disabled={isScanning}
            >
              <img src={ScanIcon} className="btn-icon" alt="" />
              {isScanning ? t("toolbar_scanning") : t("toolbar_scan_btn")}
            </button>
          )}
          {currentSession && documents.length > 0 && (
            <button className="btn ai" onClick={handleOcr}>
              <img src={OcrIcon} className="btn-icon" alt="" />
              {ocrMode === "default"
                ? t("toolbar_ocr_default")
                : t("toolbar_ocr_vip")}
            </button>
          )}
          {ocrDone && (
            <button className="btn" onClick={handleEditOrSave}>
              <img
                src={fieldsEditable ? SaveIcon : EditIcon}
                className="btn-icon"
                alt=""
              />
              {fieldsEditable
                ? t("toolbar_save_result")
                : t("toolbar_edit_index")}
            </button>
          )}
          {currentSession && documents.length > 0 && (
            <button className="btn ai" onClick={handleOcrVip}>
              <img src={OcrIcon} className="btn-icon" alt="" />
              {t("toolbar_switch_ocr_mode")}
            </button>
          )}
          <button className="btn export" onClick={handleExport}>
            <img src={UploadIcon} className="btn-icon" alt="" />
            <div> {t("toolbar_export_result")}</div>
          </button>
          <button className="btn danger" onClick={handleResetSession}>
            <img src={RefreshIcon} className="btn-icon" alt="" />
            {t("toolbar_refresh")}
          </button>
        </div>
      </div>

      {currentSession && (
        <>
          <div className="naming-summary-bar">
            <div className="naming-summary-main">
              <div className="naming-summary-label">
                {t("naming_batch_name")}
              </div>
              <div className="naming-summary-value">{namingBatchPreview}</div>
            </div>
            <div className="naming-summary-side">
              <div className={`naming-status status-${namingState.status}`}>
                {namingStatusLabel}
              </div>
              {namingDocumentCount > 0 && (
                <div className="naming-summary-count">
                  {t("naming_summary_doc_count", {
                    count: namingDocumentCount,
                  })}
                </div>
              )}
              <button
                className="btn"
                onClick={() => setShowNamingPopup(true)}
                disabled={namingBusy}
              >
                {t("naming_open_modal_btn")}
              </button>
            </div>
          </div>
          <div
            className={`naming-workflow-overlay ${showNamingPopup ? "is-open" : ""}`}
            onClick={() => setShowNamingPopup(false)}
          >
            <div
              className="naming-workflow naming-workflow-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="naming-modal-header">
                <div>
                  <div className="modal-title">{t("naming_modal_title")}</div>
                  <div className="naming-modal-subtitle">
                    {t("naming_modal_subtitle")}
                  </div>
                </div>
                <button
                  type="button"
                  className="naming-modal-close"
                  onClick={() => setShowNamingPopup(false)}
                  aria-label={t("naming_modal_close")}
                >
                  ×
                </button>
              </div>
          <section className="naming-step-card">
            <div className="naming-step-title">
              {t("naming_rules_picker_title")}
            </div>
            <div className="naming-picker-grid">
              <div className="naming-picker-section">
                <div className="naming-picker-title-row">
                  <span>{t("naming_document_rule_group_title")}</span>
                </div>
                {activeDocumentRules.length ? (
                  <>
                    <select
                      className="naming-rule-select"
                      value={sessionMetadata.selected_document_rule_code}
                      disabled={isNamingFinalized}
                      onChange={(e) => {
                        const rule = activeDocumentRules.find(
                          (item) => item.code === e.target.value,
                        );
                        if (!rule) {
                          updateSessionMetadata(
                            "selected_document_rule_code",
                            "",
                          );
                          return;
                        }
                        selectNamingRule("logical_document_name", rule);
                      }}
                    >
                      <option value="">
                        {t("naming_rule_default_option")}
                      </option>
                      {activeDocumentRules.map((rule) => (
                        <option key={rule.code} value={rule.code}>
                          {rule.code}
                        </option>
                      ))}
                    </select>
                    {selectedDocumentRule ? (
                      <div className="naming-rule-info-box">
                        <div>
                          <strong>{selectedDocumentRule.code}</strong>
                        </div>
                        <div className="naming-rule-card-meta">
                          {selectedDocumentRule.document_type ||
                            t("naming_rule_global_target")}
                        </div>
                        <div className="naming-rule-card-pattern">
                          {selectedDocumentRule.pattern || "-"}
                        </div>
                      </div>
                    ) : (
                      <div className="naming-fallback-note">
                        {t("naming_document_rule_fallback")}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="placeholder-text">
                    {t("naming_rules_empty_active")}
                  </div>
                )}
              </div>

              <div className="naming-picker-section">
                <div className="naming-picker-title-row">
                  <span>{t("naming_batch_rule_group_title")}</span>
                </div>
                {activeBatchRules.length ? (
                  <>
                    <select
                      className="naming-rule-select"
                      value={sessionMetadata.selected_batch_rule_code}
                      disabled={isNamingFinalized}
                      onChange={(e) => {
                        const rule = activeBatchRules.find(
                          (item) => item.code === e.target.value,
                        );
                        if (!rule) {
                          updateSessionMetadata("selected_batch_rule_code", "");
                          return;
                        }
                        selectNamingRule("batch_name", rule);
                      }}
                    >
                      <option value="">
                        {t("naming_rule_default_option")}
                      </option>
                      {activeBatchRules.map((rule) => (
                        <option key={rule.code} value={rule.code}>
                          {rule.code}
                        </option>
                      ))}
                    </select>
                    {selectedBatchRule ? (
                      <div className="naming-rule-info-box">
                        <div>
                          <strong>{selectedBatchRule.code}</strong>
                        </div>
                        <div className="naming-rule-card-meta">
                          {selectedBatchRule.document_type ||
                            t("naming_rule_global_target")}
                        </div>
                        <div className="naming-rule-card-pattern">
                          {selectedBatchRule.pattern || "-"}
                        </div>
                      </div>
                    ) : (
                      <div className="naming-fallback-note">
                        {t("naming_batch_rule_fallback")}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="placeholder-text">
                    {t("naming_rules_empty_active")}
                  </div>
                )}
              </div>
            </div>

            <div className="naming-paperless-note">
              {t("naming_paperless_note")}
            </div>
          </section>

          <section className="naming-step-card">
            <div className="naming-step-title">
              {t("naming_metadata_title")}
            </div>
            <div className="naming-meta-grid naming-meta-grid-compact">
              <label className="naming-field">
                <span>{t("naming_field_record_code")}</span>
                <input
                  disabled={isNamingFinalized}
                  value={sessionMetadata.record_code}
                  onChange={(e) =>
                    updateSessionMetadata("record_code", e.target.value)
                  }
                  placeholder="Ví dụ: HS-2026-001"
                />
              </label>
              <label className="naming-field">
                <span>{t("naming_field_document_type")}</span>
                <input
                  disabled={isNamingFinalized}
                  value={sessionMetadata.document_type}
                  onChange={(e) =>
                    updateSessionMetadata("document_type", e.target.value)
                  }
                  placeholder="Ví dụ: vanban_hc"
                />
              </label>
              <label className="naming-field">
                <span>{t("naming_field_physical_location")}</span>
                <input
                  disabled={isNamingFinalized}
                  value={sessionMetadata.physical_location_code}
                  onChange={(e) =>
                    updateSessionMetadata(
                      "physical_location_code",
                      e.target.value,
                    )
                  }
                  placeholder="Ví dụ: KHOA-KE12"
                />
              </label>
            </div>

            {dynamicTokenSchema.length > 0 && (
              <div className="naming-dynamic-fields">
                {dynamicTokenSchema.map((token) => {
                  const isDateField = /date/i.test(token);
                  const isMissing = missingNamingTokens.includes(token);
                  return (
                    <label
                      key={token}
                      className={`naming-field ${isMissing ? "naming-field-missing" : ""}`}
                    >
                      <span>{token}</span>
                      <input
                        type={isDateField ? "date" : "text"}
                        disabled={isNamingFinalized}
                        value={namingMetadataValues[token] ?? ""}
                        onChange={(e) =>
                          updateDynamicTokenValue(token, e.target.value)
                        }
                        placeholder={`Nhập ${token}`}
                      />
                    </label>
                  );
                })}
              </div>
            )}

            <div className="naming-actions-row">
              <button
                className="btn"
                onClick={handleSaveMetadata}
                disabled={namingBusy || isNamingFinalized}
              >
                {t("naming_btn_save_metadata")}
              </button>
              <span className="naming-meta-hint">
                {isNamingFinalized
                  ? t("naming_finalized_reset_hint")
                  : t("naming_metadata_hint")}
              </span>
            </div>
          </section>

          <section className="naming-step-card">
            <div className="naming-step-title">{t("naming_preview_title")}</div>
            <div className="naming-actions-row">
              <button
                className="btn"
                onClick={handlePreviewNaming}
                disabled={namingBusy || isNamingFinalized}
              >
                {t("naming_btn_preview")}
              </button>
              <button
                className="btn ai"
                onClick={handleFinalizeNaming}
                disabled={namingBusy || isNamingFinalized}
              >
                {t("naming_btn_finalize")}
              </button>
              <div className={`naming-status status-${namingState.status}`}>
                {namingStatusLabel}
              </div>
              {isNamingFinalized && (
                <span className="naming-locked-note">
                  {finalizedTimeLabel
                    ? t("naming_locked_note_with_time", {
                        time: finalizedTimeLabel,
                      })
                    : t("naming_locked_note")}
                </span>
              )}
            </div>

            <div className="naming-batch-name">
              <strong>{t("naming_batch_name")}:</strong>{" "}
              {namingState.batch_name || "-"}
            </div>

            <div className="naming-doc-heading">
              <strong>{t("naming_document_names_upload_label")}</strong>
            </div>

            {namingState.warnings.length > 0 && (
              <div className="naming-warning-box">
                {namingState.warnings.map((warning, idx) => (
                  <div key={`${warning}-${idx}`}>• {warning}</div>
                ))}
              </div>
            )}

            <div className="naming-doc-list">
              {namingState.documents.length ? (
                namingState.documents.map((doc, index) => (
                  <div
                    key={`${doc.doc_index}-${doc.doc_seq}`}
                    className="naming-doc-item"
                  >
                    <div>
                      {t("naming_doc_label", {
                        number: doc.doc_seq || index + 1,
                      })}
                    </div>
                    <div className="naming-doc-name">
                      {doc.logical_document_name || "-"}
                    </div>
                    {doc.warnings?.length > 0 && (
                      <div className="naming-doc-warning">
                        {doc.warnings.map((warning, idx) => (
                          <div key={`${warning}-${idx}`}>• {warning}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="placeholder-text">
                  {t("naming_preview_empty")}
                </div>
              )}
            </div>
          </section>
            </div>
          </div>
        </>
      )}

      <div className="layout">
        {/* ============ LEFT COLUMN: DOCUMENT LIST (DRAG & DROP) ============ */}
        <div className="panel left-panel">
          <div className="panel-header">{t("doc_list_title")}</div>
          <div className="panel-body">
            {documents.length === 0 ? (
              <p className="placeholder-text">{t("doc_list_no_data")}</p>
            ) : (
              // Wrap the entire drag-and-drop area with DragDropContext.
              <DragDropContext onDragEnd={onDragEnd}>
                <ul className="doc-tree">
                  {documents.map((doc) => (
                    <li key={doc.doc_index}>
                      {/* Document title */}
                      <div
                        className="doc-tree-title"
                        data-is-selected={
                          selectedDocIndex === doc.doc_index ? "" : undefined
                        }
                        onClick={() => setSelectedDocIndex(doc.doc_index)}
                      >
                        📁{" "}
                        {documentNamingMap.get(doc.doc_index) ||
                          t("doc_list_doc_item", {
                            index: doc.doc_index + 1,
                          })}
                      </div>

                      {/* Droppable area for each document */}
                      <Droppable droppableId={`doc-${doc.doc_index}`}>
                        {(provided, snapshot) => (
                          <ul
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="droppable-list"
                            data-is-dragging-over={
                              snapshot.isDraggingOver ? "" : undefined
                            }
                          >
                            {/* Page list (draggable) */}
                            {(doc.indices || []).map((imgIndex, pagePos) => {
                              const isSelected =
                                selectedImage === allImageUrls[imgIndex];
                              const uniqueKey = `${doc.doc_index}-${pagePos}-${imgIndex}`;
                              const dragId = `page-${doc.doc_index}-${pagePos}-${imgIndex}`;
                              return (
                                <Draggable
                                  key={uniqueKey}
                                  draggableId={dragId}
                                  index={pagePos}
                                >
                                  {(provided, snapshot) => (
                                    <li
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      onClick={() => {
                                        setSelectedDocIndex(doc.doc_index);
                                        setSelectedImage(
                                          allImageUrls[imgIndex],
                                        );
                                      }}
                                      className="draggable-list-item"
                                      data-is-selected={
                                        isSelected ? "" : undefined
                                      }
                                      data-is-dragging={
                                        snapshot.isDragging ? "" : undefined
                                      }
                                    >
                                      📄{" "}
                                      {t("doc_list_page_item", {
                                        index: pagePos + 1,
                                      })}
                                    </li>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </ul>
                        )}
                      </Droppable>
                    </li>
                  ))}
                </ul>
                {/* === KEEP THIS DROP ZONE AT THE BOTTOM OF THE LIST === */}
                <Droppable droppableId="create-new-doc">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`new-doc-zone ${snapshot.isDraggingOver ? "dragging-over" : ""}`}
                    >
                      {snapshot.isDraggingOver ? (
                        <span>{t("doc_list_drag_drop_hint")}</span>
                      ) : (
                        <span>{t("doc_list_drag_new_hint")}</span>
                      )}
                      {/* Hidden placeholder required for drag-and-drop behavior */}
                      <div className="droppable-dnd-placeholder">
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>

        {/* Center column: document preview */}
        <div className="panel center-panel" ref={wrapperRef}>
          <div className="panel-header">{t("preview_title")}</div>
          <TransformWrapper
            initialScale={1}
            centerOnInit={true}
            wheel={{ step: 0.2 }}
            doubleClick={{ disabled: true }}
          >
            {() => (
              <div className="center-panel">
                <div className="preview-wrapper">
                  <TransformComponent>
                    {selectedImage && (
                      <img
                        ref={imageRef}
                        src={selectedImage}
                        alt=""
                        className="preview-image"
                      />
                    )}
                  </TransformComponent>
                </div>
              </div>
            )}
          </TransformWrapper>
        </div>

        {/* Right column: indexes */}
        <div className="panel right-panel">
          <div className="panel-header">
            {t("indexes_title")}
            {currentDoc && (
              <span style={{ fontSize: 12, marginLeft: 8, color: "white" }}>
                {t("indexes_doc_ref", { index: currentDoc.doc_index + 1 })}
              </span>
            )}
          </div>

          <div className="indexes-container">
            {selectedDocIndex !== null && currentDoc && currentDoc.fields ? (
              currentDoc.fields.map((field, idx) => (
                <div key={idx} className="index-row">
                  <div className="index-label">{field.description}</div>
                  <textarea
                    className="index-textarea"
                    disabled={!fieldsEditable}
                    value={field.value || ""}
                    onChange={(e) => {
                      if (!fieldsEditable) return;
                      const value = e.target.value;
                      setDocuments((prev) =>
                        prev.map((doc) => {
                          if (doc.doc_index !== currentDoc.doc_index)
                            return doc;
                          const newFields = doc.fields.map((f, i) =>
                            i === idx ? { ...f, value } : f,
                          );
                          return { ...doc, fields: newFields };
                        }),
                      );
                    }}
                  />
                </div>
              ))
            ) : templateFields.length > 0 ? (
              templateFields.map((field, idx) => (
                <div key={idx} className="index-row">
                  <div className="index-label">{field.description}</div>
                  <textarea
                    className="index-textarea"
                    disabled={true}
                    value={""}
                  />
                </div>
              ))
            ) : (
              <p className="placeholder-text">
                {t("indexes_select_template_hint")}
              </p>
            )}
          </div>
        </div>
      </div>

      <TemplateModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={handleSelectTemplate}
      />
      <TemplateManagerModal
        open={openManagerModal}
        onClose={() => setOpenManagerModal(false)}
      />
      <CreateTemplateModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSaved={() => {
          setToastMsg(t("messages_create_template_success"));
        }}
      />
      {/* Confirm popup shown when first page scanned but rest not seen */}
      {showConfirmPopup &&
        createPortal(
          <div className="portal-container">
            <div className="modal-box">
              <div className="modal-title">
                {t("messages_confirm_scan_title")}
              </div>
              <div className="modal-content">
                {t("messages_confirm_scan_content")}
              </div>
              <div className="modal-actions">
                <button className="btn" onClick={cancelContinueScan}>
                  {t("messages_btn_cancel")}
                </button>
                <button className="btn ai" onClick={confirmContinueScan}>
                  {t("messages_btn_continue")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {showExistingPagesPopup &&
        createPortal(
          <div className="portal-container">
            <div className="modal-box">
              <div className="modal-title">Bạn muốn tiếp tục scan</div>
              <div className="modal-content">
                {t("messages_confirm_scan_content")}
              </div>
              <div className="modal-actions">
                <button className="btn" onClick={cancelContinueScan}>
                  {t("messages_btn_cancel")}
                </button>
                <button className="btn ai" onClick={() => {}}>
                  {t("messages_btn_continue")}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {globalLoading && (
        <div className="global-loading-overlay">
          <div className="global-loading-box">
            <div className="global-spinner"></div>
            <div className="global-loading-text">{loadingText}</div>
          </div>
        </div>
      )}
      <Toast message={toastMsg} onClose={() => setToastMsg("")} />
    </div>
  );
}
